import React, { ComponentType, useCallback, useEffect, useState } from 'react';
import { Fade, LinearProgress } from '@mui/material';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { styled } from '@cp/ds/src/theme';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { coreCatalogApi } from '@cp/entities/catalog';
import { Image, cssToken } from '@cp/ui';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { MediaType } from '@cp/shared/image';
import { Typography } from '@cp/ds/src/components/typography';
import { Stack } from '@cp/ds/src/components/stack';
import { FireIcon } from '@cp/ds/src/icons';

const name = 'RelevantCatalogItemsProgressBar';
const Tokens = { AccentColor: cssToken(name, ['color', 'accent']), BorderRadius: cssToken(name, ['border', 'radius']) };
const Root = styled(Box, { name })(({ theme }) => ({
  [Tokens.AccentColor]: theme.palette['brand pink'],
  [Tokens.BorderRadius]: `${theme.borderRadius.normal}px`,
}));
const ItemRoot = styled(Box, { name, slot: 'Item' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '100%',
  overflow: 'hidden',
  gap: theme.spacing(0.5),
}));

interface RelevantCatalogItemProps extends BoxProps {
  catalogItem: CatalogItemViewModel;
}
const RelevantCatalogItem = React.forwardRef<HTMLElement, RelevantCatalogItemProps>(({ catalogItem, ...props }, ref) => {
  return (
    <ItemRoot {...props} ref={ref}>
      <picture>
        <source src={catalogItem.clientProfile?.mainPhoto?.image?.webp?.src} type={MediaType.webp} />
        <Image
          alt={catalogItem.clientProfile?.name}
          src={catalogItem.clientProfile?.mainPhoto?.image?.base?.src}
          sx={{ width: 100, height: 100, borderRadius: Tokens.BorderRadius() }}
        />
      </picture>
      <Typography
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          typography: 'h3',
        }}
      >
        {catalogItem.clientProfile?.name ?? ''}
      </Typography>
      <Stack direction="column" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <FireIcon fontSize="small" />
          <Typography variant="body bold">{String(catalogItem.reviewsCount)}</Typography>
        </Stack>
        <Typography sx={{ textAlign: 'center', color: 'gray' }} variant="small text bold">
          Заказов выполнено
        </Typography>
      </Stack>
    </ItemRoot>
  );
});

export interface RelevantCatalogItemsProgressBarProps extends BoxProps {
  request: CatalogItemListRequest;
  /**
   * Take a pause between requests
   */
  throttleRequest?: number;
  onComplete?: () => void;
  ItemComponent?: ComponentType<RelevantCatalogItemProps>;
}
export const RelevantCatalogItemsProgressBar: React.FC<RelevantCatalogItemsProgressBarProps> = ({
  request,
  onComplete,
  ItemComponent = RelevantCatalogItem,
  throttleRequest = 0,
  ...props
}) => {
  const [progress, setProgress] = React.useState(0);
  const [items, setItems] = useState<CatalogItemViewModel[]>([]);
  const [currentChunkNumber, setChunkNumber] = React.useState(0);
  const [targetItemsCount, setTargetItemsCount] = React.useState(request.pagination || 3);
  const [getCatalogItems] = coreCatalogApi.endpoints.getItems.useLazyQuery();

  const loadChunk = useCallback(
    async (page: number, signal: AbortSignal) => {
      const { data } = await getCatalogItems({ ...request, pagination: 1, page });
      if (signal.aborted) {
        return;
      }
      if (!data) {
        setTargetItemsCount(0);
        return;
      }
      if (data.items) {
        setItems((loaded) => {
          const next = loaded.concat(data.items);
          setProgress(Math.round((next.length / targetItemsCount) * 100));
          return next;
        });
      }
      if (data.totalCount < targetItemsCount) {
        setTargetItemsCount(data.totalCount);
      }
    },
    [getCatalogItems, request, targetItemsCount],
  );

  useEffect(() => {
    const ac = new AbortController();
    const loadChunks = async (signal: AbortSignal) => {
      if (currentChunkNumber < targetItemsCount) {
        const nextChunk = currentChunkNumber + 1;
        await loadChunk(nextChunk, signal);
        if (!signal.aborted) {
          setChunkNumber(currentChunkNumber + 1);
        }
      } else {
        onComplete?.();
      }
    };
    const timer = setTimeout(() => loadChunks(ac.signal), currentChunkNumber ? throttleRequest : 0);
    return () => {
      ac.abort();
      clearTimeout(timer);
    };
  }, [targetItemsCount, currentChunkNumber, loadChunk, onComplete, throttleRequest]);

  return (
    <Root {...props}>
      <Box sx={{ display: 'grid', rowGap: 2, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {items.map((item) => (
          <Fade in key={item.id} timeout={throttleRequest}>
            <ItemComponent catalogItem={item} key={item.id} />
          </Fade>
        ))}
      </Box>
      <LinearProgress
        sx={{
          '& .MuiLinearProgress-bar1Determinate': {
            backgroundColor: Tokens.AccentColor(),
          },
          mt: 5,
        }}
        value={progress}
        variant="determinate"
      />
    </Root>
  );
};
RelevantCatalogItemsProgressBar.displayName = name;
